import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PaymentForm from "./components/PaymentForm";
import { useSEO, SITE_URL } from "@/hooks/useSEO";

type PlanKey = "basic" | "gold";

const plans = {
  basic: {
    key: "basic" as PlanKey,
    name: "Basic",
    label: "Basic",
    price: 10,
    period: "month",
    color: "from-gray-50 to-stone-50",
    borderColor: "border-gray-200",
    activeBorder: "border-amber-400",
    badge: null,
    icon: "ri-store-2-line",
    iconBg: "bg-gray-100 text-gray-600",
    features: [
      "List up to 50 products",
      "Standard marketplace placement",
      "Basic analytics dashboard",
      "Email support (48hr response)",
      "1 advertisement slot per month",
      "Access to buyer inquiries",
      "CrownGlobal verified badge",
    ],
    notIncluded: [
      "Priority search ranking",
      "Featured homepage placement",
      "Dedicated account manager",
    ],
  },
  gold: {
    key: "gold" as PlanKey,
    name: "Gold",
    label: "Gold",
    price: 26,
    period: "month",
    color: "from-amber-50 to-orange-50",
    borderColor: "border-amber-200",
    activeBorder: "border-amber-500",
    badge: "Best Value",
    icon: "ri-vip-crown-2-line",
    iconBg: "bg-amber-100 text-amber-600",
    features: [
      "Unlimited product listings",
      "Priority search ranking (10x visibility)",
      "Featured homepage & category placement",
      "Advanced analytics & sales reports",
      "Priority support (2hr response)",
      "5 premium advertisement slots/month",
      "Dedicated account manager",
      "Gold verified badge & trust seal",
      "Access to exclusive buyer RFQs",
      "Dropshipping integration support",
    ],
    notIncluded: [],
  },
};

type Step = "plans" | "payment" | "success";

export default function MembershipPage() {
  useSEO({
    title: "Membership Plans | Factory & Seller Subscriptions | Crown Global",
    description: "Join Crown Global with Basic ($10/mo) or Gold ($26/mo) membership. List products, access buyer requests, get verified badge, and grow your factory or business on the world's leading trade platform.",
    keywords: "membership plans, factory membership, seller subscription, Crown Global membership, trade platform subscription",
    canonical: "/membership",
    ogTitle: "Membership Plans — Crown Global Factory & Buyer",
    ogDescription: "Basic $10/mo or Gold $26/mo. List products, access buyer requests, get verified badge on Crown Global.",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/membership`,
          "url": `${SITE_URL}/membership`,
          "name": "Membership Plans — Crown Global Factory & Buyer",
          "description": "Choose a membership plan to list products and access buyer requests on Crown Global.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
              { "@type": "ListItem", "position": 2, "name": "Membership", "item": `${SITE_URL}/membership` }
            ]
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Who needs a membership?",
              "acceptedAnswer": { "@type": "Answer", "text": "All factories, sellers, suppliers, and businesses that want to list products or post advertisements on CrownGlobal must hold an active membership." }
            },
            {
              "@type": "Question",
              "name": "Can I cancel anytime?",
              "acceptedAnswer": { "@type": "Answer", "text": "Yes. You can cancel your membership at any time. Your access continues until the end of the current billing period." }
            },
            {
              "@type": "Question",
              "name": "Is my payment information secure?",
              "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. All payments are processed with 256-bit SSL encryption. We never store your card details on our servers." }
            },
            {
              "@type": "Question",
              "name": "Can I upgrade from Basic to Gold?",
              "acceptedAnswer": { "@type": "Answer", "text": "Yes! You can upgrade at any time. The price difference will be prorated for the remaining days in your billing cycle." }
            },
            {
              "@type": "Question",
              "name": "What payment methods are accepted?",
              "acceptedAnswer": { "@type": "Answer", "text": "We accept Visa, Mastercard, all major credit and debit cards, WeChat Pay, and Alipay. All currencies are converted automatically." }
            }
          ]
        }
      ]
    }
  });

  const [step, setStep] = useState<Step>("plans");
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>("gold");

  const handleChoosePlan = (plan: PlanKey) => {
    setSelectedPlan(plan);
    setStep("payment");
  };

  const handleSuccess = () => {
    setStep("success");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8">

          {/* Header */}
          {step === "plans" && (
            <>
              <div className="text-center mb-12">
                <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-2">Membership Plans</p>
                <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-3">
                  Grow Your Business on CrownGlobal
                </h1>
                <p className="text-gray-500 text-base max-w-2xl mx-auto">
                  Factories, sellers, and advertisers must hold an active membership to list products and post advertisements. Choose the plan that fits your business.
                </p>
              </div>

              {/* Plan cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-14">
                {(Object.values(plans) as typeof plans[PlanKey][]).map(plan => (
                  <div
                    key={plan.key}
                    onClick={() => setSelectedPlan(plan.key)}
                    className={`relative rounded-2xl p-8 bg-gradient-to-br ${plan.color} border-2 transition-all duration-200 cursor-pointer ${selectedPlan === plan.key ? plan.activeBorder + " scale-[1.02]" : plan.borderColor + " hover:border-amber-300"}`}
                  >
                    {plan.badge && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-amber-400 text-white text-xs font-bold px-5 py-1.5 rounded-full whitespace-nowrap">
                        {plan.badge}
                      </div>
                    )}

                    <div className="flex items-center gap-3 mb-5">
                      <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${plan.iconBg}`}>
                        <i className={`${plan.icon} text-2xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-extrabold text-gray-900">{plan.name} Membership</h3>
                        <p className="text-xs text-gray-400">For factories, sellers &amp; advertisers</p>
                      </div>
                    </div>

                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="text-5xl font-extrabold text-gray-900">${plan.price}</span>
                      <span className="text-gray-400 text-sm font-medium">/ month</span>
                    </div>

                    <ul className="flex flex-col gap-2.5 mb-6">
                      {plan.features.map(f => (
                        <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                          <i className="ri-checkbox-circle-fill text-amber-400 mt-0.5 shrink-0"></i>
                          {f}
                        </li>
                      ))}
                      {plan.notIncluded.map(f => (
                        <li key={f} className="flex items-start gap-2 text-sm text-gray-400">
                          <i className="ri-close-circle-line text-gray-300 mt-0.5 shrink-0"></i>
                          {f}
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={e => { e.stopPropagation(); handleChoosePlan(plan.key); }}
                      className={`w-full font-bold py-3.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${plan.key === "gold" ? "bg-amber-400 hover:bg-amber-500 text-white" : "bg-white hover:bg-gray-50 text-gray-800 border border-gray-200"}`}
                    >
                      {plan.key === "gold" ? (
                        <><i className="ri-vip-crown-2-line mr-2"></i>Get Gold Membership</>
                      ) : (
                        <><i className="ri-store-2-line mr-2"></i>Get Basic Membership</>
                      )}
                    </button>
                  </div>
                ))}
              </div>

              {/* Payment methods accepted */}
              <div className="flex flex-col items-center gap-4 mb-14">
                <p className="text-sm text-gray-400 font-medium">Accepted Payment Methods</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {[
                    { icon: "ri-visa-line", label: "Visa", color: "text-gray-700" },
                    { icon: "ri-mastercard-line", label: "Mastercard", color: "text-red-500" },
                    { icon: "ri-bank-card-2-line", label: "Debit Card", color: "text-gray-600" },
                    { icon: "ri-wechat-pay-line", label: "WeChat Pay", color: "text-green-500" },
                    { icon: "ri-alipay-line", label: "Alipay", color: "text-sky-500" },
                  ].map(m => (
                    <div key={m.label} className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-3">
                      <i className={`${m.icon} text-2xl ${m.color}`}></i>
                      <span className="text-sm font-semibold text-gray-700">{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div className="max-w-2xl mx-auto">
                <h2 className="text-xl font-extrabold text-gray-900 text-center mb-6">Frequently Asked Questions</h2>
                <div className="flex flex-col gap-3">
                  {[
                    { q: "Who needs a membership?", a: "All factories, sellers, suppliers, and businesses that want to list products or post advertisements on CrownGlobal must hold an active membership." },
                    { q: "Can I cancel anytime?", a: "Yes. You can cancel your membership at any time. Your access continues until the end of the current billing period." },
                    { q: "Is my payment information secure?", a: "Absolutely. All payments are processed with 256-bit SSL encryption. We never store your card details on our servers." },
                    { q: "Can I upgrade from Basic to Gold?", a: "Yes! You can upgrade at any time. The price difference will be prorated for the remaining days in your billing cycle." },
                    { q: "What payment methods are accepted?", a: "We accept Visa, Mastercard, all major credit and debit cards, WeChat Pay, and Alipay. All currencies are converted automatically." },
                  ].map((item, i) => (
                    <FaqItem key={i} q={item.q} a={item.a} />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Payment step */}
          {step === "payment" && (
            <div className="max-w-lg mx-auto">
              <PaymentForm
                plan={plans[selectedPlan]}
                onBack={() => setStep("plans")}
                onSuccess={handleSuccess}
              />
            </div>
          )}

          {/* Success step */}
          {step === "success" && (
            <div className="max-w-lg mx-auto">
              <div className="bg-white rounded-2xl p-10 text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
                  <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Payment Successful!</h2>
                <p className="text-gray-500 text-sm mb-2">
                  Welcome to CrownGlobal <strong className="text-amber-500">{plans[selectedPlan].label} Membership</strong>!
                </p>
                <p className="text-gray-400 text-xs mb-8">
                  A confirmation email has been sent to your registered email address. Your membership is now active.
                </p>

                <div className="bg-amber-50 rounded-xl p-5 mb-8 text-left">
                  <h3 className="font-bold text-gray-800 mb-3 text-sm">Your Membership Summary</h3>
                  <div className="flex flex-col gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Plan</span>
                      <span className="font-semibold text-gray-800">{plans[selectedPlan].label} Membership</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Amount</span>
                      <span className="font-semibold text-gray-800">${plans[selectedPlan].price}/month</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Next billing date</span>
                      <span className="font-semibold text-gray-800">May 10, 2026</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status</span>
                      <span className="font-semibold text-green-600 flex items-center gap-1"><i className="ri-checkbox-circle-fill"></i> Active</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <a href="/vendor-dashboard" className="block w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-center">
                    <i className="ri-dashboard-line mr-2"></i>Go to Vendor Dashboard
                  </a>
                  <button onClick={() => setStep("plans")} className="w-full border border-gray-200 text-gray-600 font-semibold py-3 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                    Back to Membership Plans
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-800 text-sm">{q}</span>
        <i className={`${open ? "ri-subtract-line" : "ri-add-line"} text-amber-400 text-lg shrink-0 ml-3`}></i>
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-gray-500 leading-relaxed border-t border-gray-50">
          {a}
        </div>
      )}
    </div>
  );
}
